import { useEffect, useRef, useState } from 'react';

/**
 * A `processing` flag that, once started, stays up for at least `minMs` before
 * the settled outcome is applied — so a fast request doesn't make a spinner
 * flash. `start()` raises the flag and returns the `commit` to call with the
 * outcome; only the exit is floored, never the entry.
 */
export function useFlooredSettle(minMs: number) {
  const [processing, setProcessing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const start = () => {
    setProcessing(true);
    const startedAt = Date.now();
    return (apply: () => void) => {
      const remaining = minMs - (Date.now() - startedAt);
      const finish = () => {
        setProcessing(false);
        apply();
      };
      if (remaining <= 0) finish();
      else timer.current = setTimeout(finish, remaining);
    };
  };

  return { processing, start };
}
